<template>
	<div class="container__powerbi" :class="{ 'fullscreen-mode': isFullscreen }">
		<div class="controls-hover-area" :class="{ 'fullscreen-controls': isFullscreen }">
			<div class="report-controls">
				<div class="report-selector">
					<label for="report-dropdown" class="report-selector__label">
						{{ $t('selectReport') }}:
					</label>
					<select
						id="report-dropdown"
						v-model="selectedReportId"
						@change="onReportChange"
						class="report-selector__dropdown"
						:disabled="loading || reports.length === 0"
					>
						<option value="" disabled>
							{{ loading ? $t('loadingReports') : $t('chooseReport') }}
						</option>
						<option
							v-for="report in reports"
							:key="report.id"
							:value="report.id"
						>
							{{ report.name }}
						</option>
					</select>
				</div>

				<div v-if="selectedReportId && !loadingReport" class="action-buttons">
					<button
						@click="printReport"
						class="print-button"
						:disabled="printing"
						:title="$t('printReport')"
					>
						<i class="fas fa-print"></i>
						{{ printing ? $t('printing') : $t('print') }}
					</button>

					<button
						@click="toggleFullscreen"
						class="fullscreen-button"
						:title="isFullscreen ? $t('exitFullscreen') : $t('viewFullscreen')"
					>
						<i :class="isFullscreen ? 'fas fa-compress' : 'fas fa-expand'"></i>
						{{ isFullscreen ? $t('exitFullscreen') : $t('fullscreen') }}
					</button>
				</div>
			</div>
		</div>

		<div v-if="loadingReport" class="loading-container">
			<div class="spinner"></div>
			<p>{{ $t('loadingReport') }}</p>
		</div>

		<section
			v-show="!loadingReport && selectedReportId"
			id="powerbi_dashboard"
			:style="dashboardStyle"
		/>

		<div v-if="!selectedReportId && !loading" class="no-report-selected">
			<p>{{ $t('noReportSelected') }}</p>
		</div>
	</div>
</template>

<script>
import * as pbi from 'powerbi-client'

import { getReports, getEmbedTokenByReportId } from '../../services/requests/DashBoardRequests'
// import { models, service } from 'powerbi-client';
const permissions = pbi.models.Permissions.All

export default {
	name: 'PowerBI',
	data() {
		return {
			reports: [],
			selectedReportId: '',
			loading: false,
			loadingReport: false,
			isFullscreen: false,
			printing: false,
			embedConfig: {
				type: 'report',
				id: '',
				embedUrl: '',
				accessToken: '',
				tokenType: pbi.models.TokenType.Embed,
				settings: {
					filterPaneEnabled: false,
					navContentPaneEnabled: true,
				},
				permissions: permissions,
				pageview: 'actualSize',
			},
			powerbiService: null,
			embeddedReport: null,
		}
	},

	computed: {
		dashboardStyle() {
			if (this.isFullscreen) {
				return {
					height: 'calc(100vh - 20px)',
					width: '100%',
					marginTop: '0',
				}
			}
			return {
				height: '780px',
				position: 'center',
				marginTop: '20px',
			}
		},
	},

	async mounted() {
		await this.loadReports()
		this.setupFullscreenListener()
	},

	methods: {
		toggleFullscreen() {
			const container = document.querySelector('.container__powerbi')

			if (!this.isFullscreen) {
				// Entrar em tela cheia
				if (container.requestFullscreen) {
					container.requestFullscreen()
				} else if (container.webkitRequestFullscreen) {
					// Safari
					container.webkitRequestFullscreen()
				} else if (container.msRequestFullscreen) {
					// IE11
					container.msRequestFullscreen()
				}
			} else {
				// Sair da tela cheia
				if (document.exitFullscreen) {
					document.exitFullscreen()
				} else if (document.webkitExitFullscreen) {
					// Safari
					document.webkitExitFullscreen()
				} else if (document.msExitFullscreen) {
					// IE11
					document.msExitFullscreen()
				}
			}
		},

		setupFullscreenListener() {
			// Listener para detectar mudanças no modo tela cheia
			const fullscreenChangeHandler = () => {
				this.isFullscreen = !!(
					document.fullscreenElement ||
					document.webkitFullscreenElement ||
					document.msFullscreenElement
				)
			}

			document.addEventListener('fullscreenchange', fullscreenChangeHandler)
			document.addEventListener('webkitfullscreenchange', fullscreenChangeHandler)
			document.addEventListener('msfullscreenchange', fullscreenChangeHandler)

			// Guarda o handler para remover depois
			this._fullscreenChangeHandler = fullscreenChangeHandler
		},

		async printReport() {
			if (!this.embeddedReport) {
				console.error('Nenhum relatório carregado para imprimir')
				return
			}

			try {
				this.printing = true

				// Utiliza a API do Power BI para imprimir o relatório
				await this.embeddedReport.print()

				console.log('Relatório enviado para impressão')
			} catch (error) {
				console.error('Erro ao imprimir relatório:', error)

				// Fallback: tenta usar o window.print() se a API do Power BI falhar
				if (error.message && error.message.includes('not supported')) {
					console.log('Tentando método alternativo de impressão...')
					window.print()
				}
			} finally {
				this.printing = false
			}
		},

		async loadReports() {
			try {
				this.loading = true
				const response = await getReports()
				this.reports = response.data || []

				// Seleciona automaticamente o primeiro relatório se houver
				if (this.reports.length > 0) {
					this.selectedReportId = this.reports[0].id
					await this.loadReport()
				}
			} catch (error) {
				console.error('Erro ao carregar lista de relatórios:', error)
				this.reports = []
			} finally {
				this.loading = false
			}
		},

		async onReportChange() {
			if (this.selectedReportId) {
				await this.loadReport()
			}
		},

		async loadReport() {
			try {
				this.loadingReport = true

				// Remove o relatório anterior se existir
				if (this.embeddedReport) {
					this.embeddedReport.off('loaded')
					this.embeddedReport.off('error')
					const reportContainer = document.getElementById('powerbi_dashboard')
					if (reportContainer) {
						reportContainer.innerHTML = ''
					}
				}

				const response = await getEmbedTokenByReportId(this.selectedReportId)
				const { embedToken } = response.data
				const embedUrl = embedToken.embedUrl
				const token = embedToken.embedToken.token
				const id = embedToken.embedToken.id

				this.embedConfig.accessToken = token
				this.embedConfig.embedUrl = embedUrl
				this.embedConfig.id = id
				this.embedConfig.settings.filterPaneEnabled = false
				this.embedConfig.settings.navContentPaneEnabled = false

				const reportContainer = document.getElementById('powerbi_dashboard')

				if (!this.powerbiService) {
					this.powerbiService = new pbi.service.Service(
						pbi.factories.hpmFactory,
						pbi.factories.wpmpFactory,
						pbi.factories.routerFactory,
					)
				}

				this.embeddedReport = this.powerbiService.embed(reportContainer, this.embedConfig)

				// Event handlers
				this.embeddedReport.on('loaded', () => {
					this.loadingReport = false
					console.log('Relatório carregado com sucesso')
				})

				this.embeddedReport.on('error', (event) => {
					this.loadingReport = false
					console.error('Erro ao carregar relatório:', event.detail)
				})
			} catch (error) {
				this.loadingReport = false
				console.error('Erro ao carregar o relatório do Power BI:', error)
			}
		},
	},

	beforeDestroy() {
		// Limpa o relatório ao destruir o componente
		if (this.embeddedReport) {
			this.embeddedReport.off('loaded')
			this.embeddedReport.off('error')
		}

		// Remove os event listeners de tela cheia
		if (this._fullscreenChangeHandler) {
			document.removeEventListener('fullscreenchange', this._fullscreenChangeHandler)
			document.removeEventListener('webkitfullscreenchange', this._fullscreenChangeHandler)
			document.removeEventListener('msfullscreenchange', this._fullscreenChangeHandler)
		}

		// Sai do modo tela cheia se estiver ativo
		if (this.isFullscreen) {
			if (document.exitFullscreen) {
				document.exitFullscreen()
			} else if (document.webkitExitFullscreen) {
				document.webkitExitFullscreen()
			} else if (document.msExitFullscreen) {
				document.msExitFullscreen()
			}
		}
	},
}
</script>

<style scoped>
.container__powerbi {
	top: -3rem;
	z-index: 0;
	padding: 0 2rem;
	height: 100%;
	max-width: 1250px;
	margin-left: auto;
	margin-right: auto;
}

.report-controls {
	display: flex;
	justify-content: space-between;
	align-items: flex-end;
	gap: 1rem;
	margin-top: 2rem;
	margin-bottom: 1rem;
	flex-wrap: wrap;
}

.action-buttons {
	display: flex;
	gap: 0.75rem;
	align-items: center;
	flex-wrap: wrap;
}

.report-selector {
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	flex: 1;
	min-width: 250px;
}

.report-selector__label {
	font-size: 1rem;
	font-weight: 600;
	color: var(--color-navy-blue, #2c3e50);
}

.report-selector__dropdown {
	width: 100%;
	max-width: 400px;
	padding: 0.75rem 1rem;
	font-size: 0.95rem;
	border: 2px solid #e0e0e0;
	border-radius: 8px;
	background-color: white;
	color: var(--color-navy-blue, #2c3e50);
	cursor: pointer;
	transition: all 0.3s ease;
	outline: none;
}

.report-selector__dropdown:hover:not(:disabled) {
	border-color: var(--color-green, #4caf50);
}

.report-selector__dropdown:focus {
	border-color: var(--color-green, #4caf50);
	box-shadow: 0 0 0 3px rgba(76, 175, 80, 0.1);
}

.report-selector__dropdown:disabled {
	background-color: #f5f5f5;
	cursor: not-allowed;
	opacity: 0.6;
}

.print-button,
.fullscreen-button {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	padding: 0.75rem 1.5rem;
	font-size: 0.95rem;
	font-weight: 600;
	color: white;
	border: none;
	border-radius: 8px;
	cursor: pointer;
	transition: all 0.3s ease;
	white-space: nowrap;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.print-button {
	background-color: var(--color-navy-blue, #2c3e50);
}

.print-button:hover:not(:disabled) {
	background-color: #34495e;
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
	transform: translateY(-1px);
}

.print-button:disabled {
	background-color: #95a5a6;
	cursor: not-allowed;
	opacity: 0.7;
}

.print-button:active:not(:disabled) {
	transform: translateY(0);
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.fullscreen-button {
	background-color: var(--color-green, #4caf50);
}

.fullscreen-button:hover {
	background-color: var(--color-green-dark, #45a049);
	box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
	transform: translateY(-1px);
}

.fullscreen-button:active {
	transform: translateY(0);
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.print-button i,
.fullscreen-button i {
	font-size: 1rem;
}

.loading-container {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	padding: 3rem;
	gap: 1rem;
}

.spinner {
	width: 50px;
	height: 50px;
	border: 4px solid #f3f3f3;
	border-top: 4px solid var(--color-green, #4caf50);
	border-radius: 50%;
	animation: spin 1s linear infinite;
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

.loading-container p {
	font-size: 1rem;
	color: var(--color-gray, #666);
	margin: 0;
}

.no-report-selected {
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 3rem;
	text-align: center;
}

.no-report-selected p {
	font-size: 1.1rem;
	color: var(--color-gray, #666);
	margin: 0;
}

/* Wrapper para área de hover dos controles */
.controls-hover-area {
	position: relative;
}

/* Estilos para modo tela cheia */
.container__powerbi.fullscreen-mode {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	width: 100vw;
	height: 100vh;
	max-width: 100vw;
	z-index: 9999;
	background-color: white;
	padding: 1rem;
	margin: 0;
}

/* Controles em modo tela cheia - ocultos por padrão */
.fullscreen-controls {
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	z-index: 10000;
	background: linear-gradient(to bottom, rgba(255, 255, 255, 0.98) 0%, rgba(255, 255, 255, 0.95) 80%, transparent 100%);
	padding: 1rem;
	padding-top: 1.5rem;
	opacity: 0;
	transform: translateY(-100%);
	transition: all 0.3s ease-in-out;
	pointer-events: none;
}

/* Área de hover expandida no topo */
.fullscreen-controls::after {
	content: '';
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	height: 50px;
	pointer-events: auto;
}

/* Mostra controles ao passar o mouse */
.fullscreen-controls:hover,
.fullscreen-controls:focus-within {
	opacity: 1;
	transform: translateY(0);
	pointer-events: auto;
}

/* Indicador visual para mostrar que há controles no topo */
.fullscreen-mode .controls-hover-area::before {
	content: '';
	position: absolute;
	top: 0;
	left: 50%;
	transform: translateX(-50%);
	width: 60px;
	height: 4px;
	background-color: var(--color-green, #4caf50);
	border-radius: 0 0 4px 4px;
	opacity: 0.6;
	z-index: 9999;
	animation: pulse-indicator 2s ease-in-out infinite;
}

/* Oculta o indicador quando os controles estão visíveis */
.fullscreen-controls:hover::before {
	opacity: 0;
}

@keyframes pulse-indicator {
	0%, 100% {
		opacity: 0.6;
		transform: translateX(-50%) scaleX(1);
	}
	50% {
		opacity: 1;
		transform: translateX(-50%) scaleX(1.2);
	}
}

.fullscreen-mode .report-controls {
	margin-top: 0.5rem;
}

.fullscreen-mode #powerbi_dashboard {
	position: absolute;
	top: 0;
	left: 0;
	width: 100% !important;
	height: 100vh !important;
	padding: 0;
	margin: 0;
}

@media screen and (max-width: 768px) {
	.container__powerbi {
		padding: 0 1rem;
	}

	.report-selector__dropdown {
		max-width: 100%;
	}

	.report-controls {
		flex-direction: column;
		align-items: stretch;
	}

	.action-buttons {
		flex-direction: column;
		width: 100%;
	}

	.print-button,
	.fullscreen-button {
		width: 100%;
		justify-content: center;
	}
}
</style>
